import { useEffect, useState, useContext } from 'react';
import SectionWrapper from '../../components/layouts/SectionWrapper';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { callAPI } from 'utils/API';
import Headline3 from 'components/elements/Headline3';
import { Alert, Box, Skeleton } from '@mui/material';
import { translate } from 'utils/Translate';
import ContentNormal from 'components/elements/ContentNormal';
import OrderTrackingOrderPart from './OrderTrackingOrderPart';
import OrderTrackingDeliveryDetails from './OrderTrackingDeliveryDetails';
import OrderTrackingHelp from './OrderTrackingHelp';
import { AppContext } from 'contexts/AppContext';
import Headline5 from 'components/elements/Headline5';
import ButtonMedium2 from 'components/elements/ButtonMedium2';

function OrderTrackingPage() {
  const { trackingToken } = useParams();
  const { brandData, styleGuide } = useContext(AppContext);
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConfirmation = async () => {
    try {
      const confirmationOrderlines = await callAPI(`/confirmation/${trackingToken}/details`);
      
      if (!confirmationOrderlines || !confirmationOrderlines.length) {
        setConfirmation(null)
        setIsLoading(false)
        return
      }

      const firstOrderlineForConfirmation = confirmationOrderlines[0]

      const orderIdToOrderlines = new Map()
      for (const orderline of confirmationOrderlines) {
        if (!orderIdToOrderlines.get(orderline.OrderID)) {
          orderIdToOrderlines.set(orderline.OrderID, [])
        }
        orderIdToOrderlines.get(orderline.OrderID).push(orderline)
      }

      const orders = []
      const orderIds = [...orderIdToOrderlines.keys()]
      for (const orderId of orderIds) {
        const variationIdToOrderlines = new Map()
        for (const orderline of orderIdToOrderlines.get(orderId)) {
          if (!variationIdToOrderlines.get(orderline.VariationID)) {
            variationIdToOrderlines.set(orderline.VariationID, [])
          }
          variationIdToOrderlines.get(orderline.VariationID).push(orderline)
        }

        const orderlinesByVariation = []
        const variationIds = [...variationIdToOrderlines.keys()]
        for (const variationId of variationIds) {
          const orderlinesForVariation = variationIdToOrderlines.get(variationId)
          const ol = orderlinesForVariation[0]
          orderlinesByVariation.push({
            OrderlineID: ol.OrderlineID,
            VariationID: ol.VariationID,
            ProductID: ol.ProductID,
            imageUrl: ol.images?.[0]
              ? ol.images[0].imageOriginSource
              : "",
            elementTitle: ol.elementTitle,
            orderlineTitle: ol.orderlineTitle,
            manufacturer: ol.manufacturer,
            variationTitle: ol.variationTitle,
            variationSize: ol.variationSize,
            variationColor: ol.colorName,
            translations: ol.translations,
            quantity: orderlinesForVariation.length,
          })
        }

        const orderOrderline = orderIdToOrderlines.get(orderId)[0]

        orders.push({
          OrderID: orderOrderline.OrderID,
          orderFlowStatus: orderOrderline.orderFlowStatus || '-',
          track: orderOrderline.orderGlsCustomerNumber,
          orderLines: orderlinesByVariation,
          trackingUrl: orderOrderline.trackingPrefix,
          trackingNumber: orderOrderline.trackingNumber,
          overrideShipmentDate: orderOrderline.overrideShipmentDate,
          expectedShipmentDate: orderOrderline.expectedShipmentDate,
          trackingEvents: orderOrderline.trackingEvents || [],
        })
      }

      const confirmation = {
        ConfirmationID: firstOrderlineForConfirmation.ConfirmationID,
        orders: orders,
        orderPaymentStatus: firstOrderlineForConfirmation.orderPaymentStatus,
      };

      if (firstOrderlineForConfirmation.customDeliveryDetails) {
        const deliveryDetails = firstOrderlineForConfirmation.customDeliveryDetails
        confirmation.orderConsignee = deliveryDetails.userName
        confirmation.orderConsigneeName = deliveryDetails.companyName
        confirmation.orderConsigneeAddress1 = deliveryDetails.address
        confirmation.orderConsigneeAddress2 = deliveryDetails.address2
        confirmation.orderConsigneeZipcode = deliveryDetails.zipcode
        confirmation.orderConsigneeCity = deliveryDetails.city
        confirmation.orderConsigneeCountry = deliveryDetails.country
        confirmation.orderConsigneePhone = `${deliveryDetails.phoneCountryCode ? `+${deliveryDetails.phoneCountryCode}` : ''}${deliveryDetails.phoneNumber}`
        confirmation.orderConsigneeEmail = deliveryDetails.emailAddress
        confirmation.shipmentReference = deliveryDetails.shipmentReference
      } else {
        confirmation.orderConsignee = firstOrderlineForConfirmation.orderConsignee
        confirmation.orderConsigneeName = firstOrderlineForConfirmation.orderConsigneeName
        confirmation.orderConsigneeAddress1 = firstOrderlineForConfirmation.orderConsigneeAddress1
        confirmation.orderConsigneeAddress2 = firstOrderlineForConfirmation.orderConsigneeAddress2
        confirmation.orderConsigneeZipcode = firstOrderlineForConfirmation.orderConsigneeZipcode
        confirmation.orderConsigneeCity = firstOrderlineForConfirmation.orderConsigneeCity
        confirmation.orderConsigneeCountry = firstOrderlineForConfirmation.orderConsigneeCountry
        confirmation.orderConsigneePhone = firstOrderlineForConfirmation.orderConsigneePhone
        confirmation.orderConsigneeEmail = firstOrderlineForConfirmation.orderConsigneeEmail
      }

      setIsLoading(false);
      setConfirmation(confirmation);
    } catch (error) {
      console.error('Failed to fetch confirmation:', error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfirmation();
  }, []);

  const brandLogoHeight = !!brandData?.features?.brandLogoHeight ? `${brandData.features.brandLogoHeight}px` : '35px';
  const headerBackgroundColor = brandData?.features?.themeOneHeaderTopBarBgColor || styleGuide.color3;
  const headerTextColor = brandData?.features?.themeOneHeaderTopBarTextColor || styleGuide.color7;

  return (
    <SectionWrapper>
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 10 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 6, width: '100%', maxWidth: 800 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%', backgroundColor: headerBackgroundColor, padding: 3, borderRadius: 4 }}>
            <img 
              style={{
                height: 'auto',
                width: 'auto',
                maxWidth: !!brandData?.features?.brandLogoWidth ? `min(${brandData.features.brandLogoWidth}, 35vw)` : '35vw',
                maxHeight: `min(${brandLogoHeight}, 50px)`,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
              src={brandData.brandLogo}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}>
              <Headline3 style={{color: headerTextColor }}>
                {"Track & Trace"}
              </Headline3>
              <Headline5 style={{color: headerTextColor }}>
                {confirmation ? `${translate('_ORDER_', 'Ordre')} #${confirmation.ConfirmationID}` : isLoading ? <Skeleton variant="text" width={150} sx={{ fontSize: 16 }} /> : ''}
              </Headline5>
            </Box>
          </Box>

          {isLoading &&
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center', width: '100%' }}>
              <Skeleton variant="text" sx={{ fontSize: 50 }} />
              <Skeleton variant="rounded" height={180}  />
              <Skeleton variant="rounded" height={300} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Skeleton variant="rounded" height={250} width={'50%'} />
                <Skeleton variant="rounded" height={250} width={'50%'} />
              </Box>
            </Box>
          }

          {!isLoading && (confirmation
            ?
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                {confirmation?.orderPaymentStatus === 'UNPAID_DELIVERY' && (
                  <Alert severity="error" sx={{borderRadius: 3}}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {translate('_ORDER_PAYMENT_UNPAID_DELIVERY_', 'Betaling for levering er ikke gennemført. Du kan betale for leveringen på din profil. Ordren vil ikke blive behandlet før betalingen er gennemført.')}
                      <Box>
                        <ButtonMedium2
                          translationKey="_ORDER_PAYMENT_UNPAID_DELIVERY_BUTTON_"
                          fallbackText="Gå til profil"
                          onClick={() => navigate('/profile/orders')}
                        />
                      </Box>
                    </Box>
                  </Alert>
                )}
                {confirmation.orders.map((order, index) => (
                  <OrderTrackingOrderPart
                    key={order.OrderID}
                    order={order}
                    index={index + 1}
                  />
                ))}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <OrderTrackingDeliveryDetails confirmation={confirmation} />
                  <OrderTrackingHelp />
                </Box>
              </Box>
            :
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <ContentNormal
                    translationKey="_ORDER_TRACKING_NO_TRACKING_"
                    fallbackText="Kunne ikke finde tracking på denne ordre."
                    style={{ textAlign: 'center' }}
                  />
                  <ContentNormal
                    translationKey="_ORDER_TRACKING_CHECK_LINK_"
                    fallbackText="Tjek venligst, at linket er korrekt."
                    style={{ textAlign: 'center' }}
                  />
                </Box>
                <OrderTrackingHelp />
              </Box>
          )}
        </Box>
      </Box>
    </SectionWrapper>
  );
}

export default OrderTrackingPage;